import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { X, Volume2, VolumeX } from 'lucide-react';
import { userAPI } from '../lib/api';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

export function RewardedGate({ 
  seconds = 15, 
  onFinish, 
  onComplete, 
  onCancel,
  title,
  description,
  children 
}) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(seconds);
  const [canContinue, setCanContinue] = useState(false);
  const [ad, setAd] = useState(null);
  const [loadingAd, setLoadingAd] = useState(true);
  const [muted, setMuted] = useState(true);
  const hasCalledComplete = useRef(false);
  const videoRef = useRef(null);

  const handleComplete = onComplete || onFinish;

  // Fetch active ad on mount
  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await userAPI.getManualAds();
        const ads = res.data?.ads || [];
        if (ads.length > 0) {
          // Pick random active ad (prefer video over image)
          const videoAds = ads.filter(a => a.type === 'video' && a.video_url);
          const imageAds = ads.filter(a => a.type === 'banner' && a.image_url);
          const selectedAd = videoAds.length > 0
            ? videoAds[Math.floor(Math.random() * videoAds.length)]
            : imageAds.length > 0
              ? imageAds[Math.floor(Math.random() * imageAds.length)]
              : null;
          setAd(selectedAd);
        }
      } catch (error) {
        console.error('Failed to fetch ad:', error);
      } finally {
        setLoadingAd(false);
      }
    };
    fetchAd();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanContinue(true);
    }
  }, [countdown]);

  // Auto-complete when countdown finishes (only once, and only if no children)
  useEffect(() => {
    if (canContinue && !children && handleComplete && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      setTimeout(() => {
        handleComplete();
      }, 300);
    }
  }, [canContinue, children, handleComplete]);

  const handleAdClick = () => {
    if (ad?.click_url) {
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted(!muted);
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }
  };

  // Brief loading state when auto-completing
  if (canContinue && !children && handleComplete) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="rewarded-gate">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">✓</span>
          </div>
          <p className="text-white font-medium">{t('ad.loadingAnswers', 'Loading answers...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="rewarded-gate">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-md w-full overflow-hidden relative">
        {/* Top bar with countdown and close */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900/50">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
              canContinue ? 'bg-teal-500' : 'bg-slate-600'
            }`}>
              {canContinue ? '✓' : countdown}
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{title || t('ad.break', 'Ad Break')}</p>
              <p className="text-xs text-slate-400">
                {canContinue ? t('ad.complete', 'Ad complete!') : t('ad.remaining', '{{s}}s remaining', { s: countdown })}
              </p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-white transition-colors p-1"
              data-testid="close-ad-gate"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Ad content area */}
        <div className="relative bg-black" style={{ minHeight: '280px' }}>
          {loadingAd ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : ad?.type === 'video' && ad.video_url ? (
            <div className="relative" onClick={handleAdClick} style={{ cursor: ad.click_url ? 'pointer' : 'default' }}>
              <video
                ref={videoRef}
                src={resolveUrl(ad.video_url)}
                autoPlay
                muted={muted}
                loop
                playsInline
                className="w-full object-contain"
                style={{ maxHeight: '320px' }}
                data-testid="ad-video"
              />
              {/* Mute toggle */}
              <button
                onClick={toggleMute}
                className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm rounded-full p-2 text-white hover:bg-black/80 transition-colors"
                data-testid="mute-toggle"
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              {ad.click_url && (
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-xs text-white/80">{t('ad.tapMore', 'Tap to learn more')}</span>
                </div>
              )}
            </div>
          ) : ad?.type === 'banner' && ad.image_url ? (
            <div
              onClick={handleAdClick}
              className="flex items-center justify-center"
              style={{ minHeight: '280px', cursor: ad.click_url ? 'pointer' : 'default' }}
            >
              <img
                src={resolveUrl(ad.image_url)}
                alt={ad.name || t('ad.alt', 'Advertisement')}
                className="w-full object-contain"
                style={{ maxHeight: '320px' }}
                data-testid="ad-image"
              />
              {ad.click_url && (
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-xs text-white/80">{t('ad.tapMore', 'Tap to learn more')}</span>
                </div>
              )}
            </div>
          ) : (
            /* Fallback placeholder when no ads exist */
            <div className="flex flex-col items-center justify-center text-center p-8" style={{ minHeight: '280px' }}>
              <div className="w-16 h-16 bg-slate-700 rounded-2xl flex items-center justify-center mb-4">
                <span className="text-2xl">📺</span>
              </div>
              <p className="text-sm text-slate-500">{t('ad.placeholder', 'Ad Placeholder')}</p>
              <p className="text-xs text-slate-600 mt-1">{description || t('ad.pleaseWait', 'Please wait {{s}} seconds...', { s: countdown })}</p>
            </div>
          )}

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-700">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-1000 ease-linear"
              style={{ width: `${((seconds - countdown) / seconds) * 100}%` }}
            />
          </div>
        </div>

        {/* Bottom actions */}
        <div className="px-4 py-3 bg-slate-900/50">
          {canContinue && handleComplete ? (
            <Button
              onClick={handleComplete}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
              data-testid="continue-button"
            >
              {t('ad.continue', 'Continue')}
            </Button>
          ) : onCancel && !canContinue ? (
            <button
              onClick={onCancel}
              className="w-full text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
            >
              {t('ad.skip', 'Skip')}
            </button>
          ) : (
            <div className="text-center py-2">
              <p className="text-xs text-slate-500">{t('ad.completeIn', 'Ad will complete in {{s}}s', { s: countdown })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
